"use client";

import { useEffect, useState } from "react";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import {
  Settings,
  ShieldCheck,
  Bell,
  Languages,
  Accessibility,
  HardDrive,
  Key,
  User,
  Monitor,
  Sun,
  Moon,
  Zap,
  ChevronRight,
  Database,
  BookOpen,
  Save,
  Loader2,
  Crown,
  Sparkles,
  Check,
  AlertCircle,
  GraduationCap,
  Volume2,
} from "lucide-react";
import { clsx } from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";
import api, {
  UserProfile,
  UserSettings,
  UserQuota,
  apiFetch,
  versioned,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";

export default function SettingsHubPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [quota, setQuota] = useState<UserQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [activeSection, setActiveSection] = useState<
    "profile" | "preferences" | "subscription"
  >("profile");

  // Profile Form States
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [department, setDepartment] = useState("");
  const [level, setLevel] = useState("200");
  const [bio, setBio] = useState("");

  // Notification Toggles
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyInApp, setNotifyInApp] = useState(true);
  const [notifyReplies, setNotifyReplies] = useState(true);
  const [audioSpeed, setAudioSpeed] = useState("1.0");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(false);
  const [appLockEnabled, setAppLockEnabled] = useState(false);
  const [cachePref, setCachePref] = useState<"auto" | "none">("auto");

  useEffect(() => {
    async function loadData() {
      try {
        const [profileData, settingsData, quotaData] = await Promise.all([
          api.getUserProfile().catch(() => null),
          api.getSettings().catch(() => null),
          apiFetch<UserQuota>(versioned("/api/v1/user/quota")).catch(
            () => null,
          ),
        ]);

        if (profileData) {
          setProfile(profileData);
          setFullName(profileData.full_name || profileData.name || "");
          setUsername(profileData.username || "");
          setDepartment(profileData.department || "");
          setLevel(String(profileData.level || "200"));
          setBio(profileData.bio || "");
        }

        if (settingsData) {
          setSettings(settingsData as any as UserSettings);
          const notif = (settingsData as any)?.notifications;
          if (notif) {
            setNotifyEmail(Boolean(notif.email ?? true));
            setNotifyInApp(Boolean(notif.inApp ?? true));
            setNotifyReplies(Boolean(notif.replies ?? true));
          }
        }

        // Load local preferences (sound, mic, app lock) from localStorage
        if (typeof window !== "undefined") {
          const savedSound = localStorage.getItem("uniui_pref_sound");
          const savedMic = localStorage.getItem("uniui_pref_microphone");
          const savedLock = localStorage.getItem("uniui pref appLock");
          if (savedSound !== null) setSoundEnabled(savedSound === "1");
          if (savedMic !== null) setMicEnabled(savedMic === "1");
          if (savedLock !== null) setAppLockEnabled(savedLock === "1");
          const savedCache = localStorage.getItem("uniui pref cache");
          if (savedCache === "none" || savedCache === "auto")
            setCachePref(savedCache);
        }

        if (quotaData) {
          setQuota(quotaData as UserQuota);
        }
      } catch (err) {
        console.error("Failed to load settings data", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.updateProfile({
        full_name: fullName.trim(),
        department_code: department.trim(),
        level: String(parseInt(level, 10) || 200),
        bio: bio.trim(),
      });
      toast.success("Profile credentials updated successfully.");
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              full_name: fullName.trim(),
              department: department.trim(),
              level: String(parseInt(level, 10) || 200),
              bio: bio.trim(),
            }
          : null,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleToggleNotification = async (
    key: "email" | "inApp" | "replies",
    val: boolean,
  ) => {
    if (key === "email") setNotifyEmail(val);
    if (key === "inApp") setNotifyInApp(val);
    if (key === "replies") setNotifyReplies(val);

    try {
      await api.updateUserSettings({
        notifications: {
          email: key === "email" ? val : notifyEmail,
          inApp: key === "inApp" ? val : notifyInApp,
          replies: key === "replies" ? val : notifyReplies,
          payments: true,
          approvals: false,
          exam_reminders: true,
          course_announcements: true,
        },
      });
      toast.success("Preferences saved.");
    } catch {
      toast.error("Failed to save preference.");
    }
  };

  const handleTogglePreference = (
    key: "sound" | "microphone" | "appLock",
    val: boolean,
  ) => {
    if (key === "sound") setSoundEnabled(val);
    if (key === "microphone") setMicEnabled(val);
    if (key === "appLock") setAppLockEnabled(val);

    try {
      if (typeof window !== "undefined") {
        localStorage.setItem(`uniui pref ${key}`, val ? "1" : "0");
      }
      toast.success(
        key === "sound"
          ? val
            ? "Sound effects enabled."
            : "Sound effects muted."
          : key === "microphone"
            ? val
              ? "Microphone access enabled."
              : "Microphone access disabled."
            : val
              ? "App lock enabled."
              : "App lock disabled.",
      );
    } catch {
      toast.error("Failed to save preference.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center text-muted-foreground space-y-4">
        <Loader2 className="animate-spin text-y" size={36} />
        <p className="text-sm font-medium tracking-wide uppercase">
          Loading System Settings…
        </p>
      </div>
    );
  }

  const isPremium = Boolean(quota?.is_premium);

  return (
    <PageTransition>
      <div className="min-h-screen bg-background text-foreground selection:bg-y selection:text-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-32 md:pb-16 space-y-10 overflow-x-hidden">
          {/* Header Banner */}
          <div className="relative rounded-3xl border border-border bg-gradient-to-b from-card/90 via-background/80 to-background p-8 overflow-hidden shadow-2xl backdrop-blur-xl">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-y/10 border border-y/20 text-y-light text-xs font-semibold uppercase tracking-wider">
                  <Settings size={14} className="text-y" />
                  Account & System Preferences
                </div>
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground uppercase font-sans">
                  Settings <span className="text-y">& Profile</span>
                </h1>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  Manage your academic credentials, departmental standing,
                  quotas, and notification alerts.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Link href="/settings/security">
                  <Button
                    variant="outline"
                    className="rounded-2xl border-border bg-card text-foreground gap-2"
                  >
                    <ShieldCheck size={16} />
                    Security & 2FA
                  </Button>
                </Link>
                <Link href="/pay">
                  <Button className="rounded-2xl bg-y hover:bg-y-light text-black font-bold gap-2 shadow-lg shadow-y/20">
                    <Crown size={16} />
                    {isPremium ? "Manage Plan" : "Upgrade Plan"}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="mt-8 pt-6 border-t border-border/80 flex flex-wrap gap-2">
              <button
                onClick={() => setActiveSection("profile")}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${
                  activeSection === "profile"
                    ? "bg-y text-black shadow-md shadow-y/20"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <User size={15} />
                Student Profile
              </button>

              <button
                onClick={() => setActiveSection("subscription")}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${
                  activeSection === "subscription"
                    ? "bg-y text-black shadow-md shadow-y/20"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <Crown size={15} />
                Plan & Quotas
              </button>

              <button
                onClick={() => setActiveSection("preferences")}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 ${
                  activeSection === "preferences"
                    ? "bg-y text-black shadow-md shadow-y/20"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                <Bell size={15} />
                Notifications & Audio
              </button>
            </div>
          </div>

          {/* Section: Profile Form */}
          {activeSection === "profile" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="rounded-3xl border border-border bg-card/50 p-6 sm:p-8 backdrop-blur-xl">
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  <div className="flex items-center justify-between pb-4 border-b border-border">
                    <div>
                      <h3 className="text-lg font-bold text-foreground uppercase">
                        Academic Identity
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Your visible student name, level, and departmental tag
                        across communities.
                      </p>
                    </div>
                    <Button
                      type="submit"
                      disabled={savingProfile}
                      className="rounded-xl bg-y hover:bg-y-light text-black font-bold gap-2 shadow-md shadow-y/20"
                    >
                      {savingProfile ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Save size={16} />
                      )}
                      Save Changes
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                        Full Name
                      </label>
                      <Input
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="e.g. Chukwuemeka Obi"
                        className="bg-background border-border rounded-xl text-sm focus:border-y/50"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                        Username (Handle)
                      </label>
                      <Input
                        value={username}
                        disabled
                        className="bg-background/60 border-border text-muted-foreground rounded-xl text-sm cursor-not-allowed"
                      />
                      <span className="text-[11px] text-muted-foreground">
                        Handle cannot be changed after registration.
                      </span>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                        Department / School
                      </label>
                      <Input
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        placeholder="e.g. Mechanical Engineering (SEET)"
                        className="bg-background border-border rounded-xl text-sm focus:border-y/50"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                        Academic Level
                      </label>
                      <select
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                        className="w-full h-10 px-3 bg-background border border-border rounded-xl text-sm text-foreground focus:border-y/50 focus:outline-none"
                      >
                        <option value="100">100 Level</option>
                        <option value="200">200 Level</option>
                        <option value="300">300 Level</option>
                        <option value="400">400 Level</option>
                        <option value="500">500 Level</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                      Academic Bio & Focus
                    </label>
                    <Textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      rows={3}
                      placeholder="Tell fellow scholars your research interests, courses revising, or strengths..."
                      className="bg-background border-border rounded-xl text-sm focus:border-y/50 resize-none"
                    />
                  </div>
                </form>
              </Card>
            </motion.div>
          )}

          {/* Section: Subscription & Quota */}
          {activeSection === "subscription" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Active Plan Card */}
                <Card className="rounded-3xl border border-y/30 bg-gradient-to-b from-y/5 to-card/60 p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-y uppercase tracking-wider font-mono">
                      Current Tier
                    </span>
                    <Badge className="bg-y text-black font-bold uppercase text-xs">
                      {quota?.plan || "Free Tier"}
                    </Badge>
                  </div>
                  <h3 className="text-2xl font-black text-foreground">
                    {isPremium ? "Premium Scholar" : "Free Scholar"}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {isPremium
                      ? "You have full unlimited academic AI access, unlimited study group creation, and priority server access."
                      : "Standard access with 5 daily questions and up to 3 active study groups."}
                  </p>
                  <Link href="/pay">
                    <Button className="w-full rounded-xl bg-y hover:bg-y-light text-black font-bold text-xs mt-2">
                      {isPremium ? "Extend Subscription" : "Upgrade to Premium"}
                    </Button>
                  </Link>
                </Card>

                {/* Question Quotas Card */}
                <Card className="rounded-3xl border border-border bg-card/50 p-6 space-y-4">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider font-mono">
                    Daily AI Questions
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-3xl font-black text-foreground font-mono">
                      {quota?.free_remaining ?? 5}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        / {quota?.daily_limit ?? 5} daily
                      </span>
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Extra_credits:{" "}
                      <span className="font-bold text-y">
                        {quota?.extra_credits ?? 0} questions
                      </span>
                    </p>
                  </div>
                  <Link href="/pay">
                    <Button
                      variant="outline"
                      className="w-full rounded-xl border-border bg-card text-foreground text-xs"
                    >
                      Top-Up Credits
                    </Button>
                  </Link>
                </Card>

                {/* Study Groups Quota Card */}
                <Card className="rounded-3xl border border-border bg-card/50 p-6 space-y-4">
                  <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider font-mono">
                    Study Groups Quota
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-3xl font-black text-foreground font-mono">
                      {quota?.study_groups_owned ?? 0}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        / {isPremium ? "Unlimited" : "3 Free"}
                      </span>
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Locked groups:{" "}
                      <span className="font-bold text-y">
                        {quota?.study_groups_locked_count ?? 0}
                      </span>
                    </p>
                  </div>
                  <Link href="/study-groups">
                    <Button
                      variant="outline"
                      className="w-full rounded-xl border-emerald-800/60 bg-emerald-950/40 text-emerald-300 text-xs"
                    >
                      Manage Study Groups
                    </Button>
                  </Link>
                </Card>
              </div>
            </motion.div>
          )}

          {/* Section: Preferences & Notifications */}
          {activeSection === "preferences" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <Card className="rounded-3xl border border-border bg-card/50 p-6 sm:p-8 space-y-6">
                <h3 className="text-lg font-bold text-foreground uppercase pb-4 border-b border-border">
                  Notification Alerts & Audio
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Email Course Notifications
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Receive exam_tips, promo_credits, and course rep
                        updates.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifyEmail}
                      onChange={(e) =>
                        handleToggleNotification("email", e.target.checked)
                      }
                      className="w-5 h-5 accent-y rounded cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        In-App Live Alerts
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Receive banner notifications for replies in community
                        chat rooms.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifyInApp}
                      onChange={(e) =>
                        handleToggleNotification("inApp", e.target.checked)
                      }
                      className="w-5 h-5 accent-y rounded cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Study Group Mention Alerts
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Notify me when cohort members post new questions in my
                        study groups.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifyReplies}
                      onChange={(e) =>
                        handleToggleNotification("replies", e.target.checked)
                      }
                      className="w-5 h-5 accent-y rounded cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Sound Effects
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Play audio_feedback_for_actions, messages, and quest
                        completions.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={soundEnabled}
                      onChange={(e) =>
                        handleTogglePreference("sound", e.target.checked)
                      }
                      className="w-5 h-5 accent-y rounded cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Microphone Access
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Allow voice input for AI tutor questions and search.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={micEnabled}
                      onChange={(e) =>
                        handleTogglePreference("microphone", e.target.checked)
                      }
                      className="w-5 h-5 accent-y rounded cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        App Lock (Password / Biometric)
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Require a passcode or biometric to open the app.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={appLockEnabled}
                      onChange={(e) =>
                        handleTogglePreference("appLock", e.target.checked)
                      }
                      className="w-5 h-5 accent-y rounded cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        LLM Response Cache
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Reuse previous AI answers for identical questions. Set
                        to Off to always generate a fresh answer.
                      </p>
                    </div>
                    <select
                      value={cachePref}
                      onChange={(e) => {
                        const next =
                          e.target.value === "none" ? "none" : "auto";
                        setCachePref(next);
                        try {
                          if (typeof window !== "undefined") {
                            localStorage.setItem("uniui pref cache", next);
                          }
                          toast.success(
                            next === "none"
                              ? "LLM cache disabled."
                              : "LLM cache enabled (auto).",
                          );
                        } catch {
                          toast.error("Failed to save preference.");
                        }
                      }}
                      className="bg-card border border-border rounded-xl px-3 py-1.5 text-xs text-foreground"
                    >
                      <option value="auto">Auto (recommended)</option>
                      <option value="none">Off (no cache)</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-background border border-border">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        TTS Podcast Speed
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Speed multiplier for audio lecture recaps.
                      </p>
                    </div>
                    <select
                      value={audioSpeed}
                      onChange={(e) => {
                        setAudioSpeed(e.target.value);
                        toast.success(`Audio speed set to ${e.target.value}x`);
                      }}
                      className="bg-card border border-border rounded-xl px-3 py-1.5 text-xs text-foreground"
                    >
                      <option value="0.75">0.75x (Slower)</option>
                      <option value="1.0">1.0x (Normal)</option>
                      <option value="1.25">1.25x (Fast)</option>
                      <option value="1.5">1.5x (Faster)</option>
                    </select>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </PageTransition>
  );
}
